import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { generateCalculation } from "app/global/calculation";
import { applyRelations } from "app/global/fieldrelation";
import { GlobalService } from "app/global/global.service";
import { ValidationHandlerService } from "app/global/validation-handler/validation-handler.service";
import { FieldInfo } from "app/interfaces/field-base";
import { Field } from "app/interfaces/fields";
import { MomentPipe } from "app/pipes/moment.pipe";
import { lastValueFrom, Observable } from "rxjs";

@Injectable({
    providedIn: 'root'
})

export class FindAndReplaceService {
    private renderer: Renderer2;
    
    constructor(
        private globalService: GlobalService,
        private _dateFormatPipe$: MomentPipe,
        rendererFactory: RendererFactory2,
        private validationHandlerService: ValidationHandlerService
    ) { this.renderer = rendererFactory.createRenderer(null, null); }



    findAndReplace(currentUuid: string|number, existField: Field): Observable<void> {
        return new Observable(observer => {
            this.globalService.updateField(currentUuid, { value: existField.value, pre_checked: existField.pre_checked, field_required: existField.field_required, selected_for_role_id: existField.selected_for_role_id, secret_hidden_field: (existField?.secret_hidden_field || 0) }, (field: Field) => {
                this.autoFillIfMatch(field);
            }, true);
            observer.next();  // Signal that the operation completed successfully
            observer.complete();
        })
    }



    autoFillIfMatch(field: Field) {
        const dragFields = document.querySelector(`.drag-fields[uuid="${field.uuid}"]`);
        if (dragFields && (field.field_info === FieldInfo.DOCUMENT_FIELD || field.field_info === FieldInfo.CUSTOM_FIELD || field.field_info === FieldInfo.ME)) {
            this.renderer.setAttribute(dragFields, 'field_required', field.field_required);
            const detailValue = field.value;
            switch (field.type) {
                case 'text':
                    this.handleTextField(field, detailValue);
                    break;
                case 'datepicker':
                    this.handleDatepickerField(field, detailValue);
                    break;
                case 'droplist':
                case 'list':
                    this.handleDroplistField(field, detailValue);
                    break;
                case 'checkbox':
                    this.handleCheckBoxField(field, detailValue);
                    break;
                // Add more cases as needed
                default:
                    console.warn(`Unhandled field type: ${field.type}`);
            }
            this.validationHandlerService.resetFieldHighlight(field.uuid, false);
        }
    }

    // Handling functions for each text field
    handleTextField(field: Field, value: string) {
        const selector = document.querySelector('.drag-fields[uuid="' + field.uuid + '"]');
        if (selector) {
            const inputSelector = selector.querySelector('.text-tag-editor, .text-field-input, .text-field-textarea');
            if (field.type_of == "tag") {
                const tagView = selector.querySelector('span.text-tag');
                this.renderer.setProperty(tagView, 'textContent', !value ? this.globalService.generateTagText(field.uuid) : (value || ''));
            }
            this.renderer.setAttribute(inputSelector, 'value', value || '');
            this.renderer.setProperty(inputSelector, 'value', value || '');
            if (field.allow_multi_lines) {
                this.renderer.setProperty(inputSelector, 'textContent', value || '');
            }
            if (inputSelector.classList.contains('calculation')) {
                generateCalculation(field.uuid, this.globalService, true);
            }
            applyRelations(field.uuid, this.globalService);
        }
    }

    // Handling functions for each datepicker field
    async handleDatepickerField(field: Field, value: string) {
        const dateStr = await lastValueFrom(this._dateFormatPipe$.transform(value) as Observable<string>);
        const isValidDate = this.isValidDate(dateStr);
        const selector = document.querySelector('.drag-fields[uuid="' + field.uuid + '"]');
        if (selector) {
            if (field.type_of == "tag") {
                const tagView = selector.querySelector('span.tag-date');
                this.renderer.setProperty(tagView, 'textContent', !value ? this.globalService.generateTagText(field.uuid) : dateStr);
            } else {
                const inputSelector = selector.querySelector('.datepicker-field');
                if (inputSelector['_flatpickr'] && value) {
                    inputSelector['_flatpickr'].setDate(value);
                }
                this.renderer.setAttribute(inputSelector, 'value', isValidDate ? dateStr : '');
                this.renderer.setProperty(inputSelector, 'value', isValidDate ? dateStr : '');
            }
            applyRelations(field.uuid, this.globalService);
        }
    }

    // Handling functions for each droplist|list field
    handleDroplistField(field: Field, value: string | string[]) {
        const selector = document.querySelector('.drag-fields[uuid="' + field.uuid + '"]');
        if (selector) {
            if (field.type == "droplist") {
                const selectedOptionLabel = selector.querySelector('.selected-option-label');
                if(field?.multi_select === 1 && field.tag_view == "0" && (value && value?.length)){
                    const multiselect = Array.isArray(value) ? value.map(v => `<span class="multiselect-selected-options">${v}</span>`).join('') : '';
                    this.renderer.setProperty(
                        selectedOptionLabel, 
                        'innerHTML',
                        multiselect
                    );
                } else {
                    this.renderer.setProperty(selectedOptionLabel, 'textContent', (field.tag_view == "1" && !value) ? this.globalService.generateTagText(field.uuid) : value);
                }
                applyRelations(field.uuid, this.globalService);
            } else if (field.type == "list") {
                if (field.multi_choice == "0") {
                    const selectedOption = field.options.find((option) => value == option.name || value == option.value);
                    if (selectedOption) {
                        const multiChoiceItem = selector.querySelector('.multiChoice-item[option_uuid="' + selectedOption.id + '"]') as HTMLElement;
                        const parentNode = multiChoiceItem.closest('.multichoice-field') as HTMLElement;
                        parentNode.querySelectorAll('.multiChoice-item').forEach((multiChoiceItem) => {
                            this.renderer.removeClass(multiChoiceItem, 'multiChoice-active');
                        });
                        this.renderer.addClass(multiChoiceItem, 'multiChoice-active');
                        applyRelations(field.uuid, this.globalService);
                    }
                } else if (field.multi_choice == "1") {
                    selector.querySelectorAll('.multiChoice-item').forEach((multiChoiceItem) => {
                            this.renderer.removeClass(multiChoiceItem, 'multiChoice-active');
                    });
                    for (const val of value) {
                        const selectedOption = field.options.find((option) => val == option.value || val == option.name);
                        if(selectedOption){
                            const multiChoiceItem = selector.querySelector('.multiChoice-item[option_uuid="' + selectedOption?.id + '"]') as HTMLElement;
                            this.renderer.addClass(multiChoiceItem, 'multiChoice-active');
                        }
                    }
                    applyRelations(field.uuid, this.globalService);
                }
            }
        }
    }

    handleCheckBoxField(field: Field, value: string) {
        const selector = document.querySelector('.drag-fields[uuid="' + field.uuid + '"]');
        if (selector) {
            if (!value) {
                this.renderer.removeClass(selector, 'checkbox-prechecked');
            } else {
                this.renderer.addClass(selector, 'checkbox-prechecked');
            }
            if (this.globalService.fields_data[field?.uuid]) {
                applyRelations(field.uuid, this.globalService);
            }
        }
    }

    private isValidDate(dateString: string): boolean {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    }
}